import React, { useState, useEffect } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Badge } from "primereact/badge";
import { toastify } from "../../../../utils/toast";
import { getItem } from "../../../../utils/localStorage";
import { makeRequest2 } from "../../../../utils/makeRequest";
import { getPropertyManagerContractByIdURL, getGLAccountsURL } from "../../../../utils/urls";

const PropertyManagerContractDetailsDialog = ({ visible, onHide, contractData, contractId }) => {
    const [contractDetails, setContractDetails] = useState(null);
    const [glAccounts, setGlAccounts] = useState([]);
    const [loading, setLoading] = useState(false);

    // Fetch GL accounts to resolve account names
    const fetchGLAccounts = async () => {
        try {
            const facilityId = await getItem("selectedFacilityId");
            const response = await makeRequest2(`${getGLAccountsURL}/${facilityId}`, "GET");
            if (response?.success) {
                setGlAccounts(response.data?.data || []);
            }
        } catch (error) {
            console.error("Error fetching GL accounts:", error);
            setGlAccounts([]);
        }
    };

    // Fetch contract details if we only have contractId
    const fetchContractDetails = async () => {
        if (!contractId) return;

        try {
            setLoading(true);
            const facilityId = await getItem("selectedFacilityId");
            
            if (!facilityId) {
                toastify("No facility selected", "error");
                return;
            }

            console.log('Fetching contract details:', contractId);

            const response = await makeRequest2(
                `${getPropertyManagerContractByIdURL}/${facilityId}/${contractId}`, 
                'GET'
            );

            console.log('Contract details response:', response);

            // Handle the response structure based on your logs
            let contractDetailsData = null;
            
            if (response?.success && response.data) {
                if (response.data.data && response.data.data.contract) {
                    contractDetailsData = response.data.data.contract;
                } else if (response.data.contract) {
                    contractDetailsData = response.data.contract;
                } else {
                    contractDetailsData = response.data;
                }
            } else if (response?.error && response.error.includes("200")) {
                // Handle case where 200 is treated as error
                contractDetailsData = response.data || response;
                if (contractDetailsData && contractDetailsData.data && contractDetailsData.data.contract) {
                    contractDetailsData = contractDetailsData.data.contract;
                }
            }

            if (contractDetailsData) {
                setContractDetails(contractDetailsData);
                console.log('Contract details loaded successfully');
            } else {
                toastify("Failed to load contract details", "error");
                console.error('Failed to load contract details:', response);
            }
        } catch (error) {
            console.error('Error fetching contract details:', error);
            toastify("Error fetching contract details: " + error.message, "error");
        } finally {
            setLoading(false);
        }
    };

    // Initialize contract details when dialog opens
    useEffect(() => {
        if (visible) {
            if (contractData) {
                // If we already have contract data, use it directly
                console.log('Using provided contract data:', contractData);
                setContractDetails(contractData);
                setLoading(false);
                // Fetch GL accounts to resolve names
                fetchGLAccounts();
            } else if (contractId) {
                // If we only have contractId, fetch the details
                fetchContractDetails();
                // Also fetch GL accounts
                fetchGLAccounts();
            }
        }
    }, [visible, contractData, contractId]);

    // Formatting functions
    const formatCurrency = (amount) => {
        if (amount === null || amount === undefined) return 'KES 0.00';
        const numAmount = Number(amount);
        if (isNaN(numAmount)) return 'KES 0.00';
        
        return `KES ${numAmount.toLocaleString('en-US', { 
            minimumFractionDigits: 2, 
            maximumFractionDigits: 2 
        })}`;
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            return new Date(dateString).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });
        } catch (error) {
            return 'Invalid Date';
        }
    };

    // Status badge component with better visibility
    const StatusBadge = ({ status }) => {
        const statusConfig = {
            'Active': { severity: 'success', label: 'Active' },
            'Inactive': { severity: 'secondary', label: 'Inactive' },
            'Completed': { severity: 'info', label: 'Completed' },
            'Suspended': { severity: 'warning', label: 'Suspended' },
            'Terminated': { severity: 'danger', label: 'Terminated' }
        };
        
        const config = statusConfig[status] || statusConfig['Inactive'];
        
        return (
            <Badge 
                value={config.label} 
                severity={config.severity}
            />
        );
    };

    // Get property manager name
    const getPropertyManagerName = () => {
        if (contractDetails?.propertyManagerDetails) {
            const pm = contractDetails.propertyManagerDetails;
            return pm.fullName || `${pm.firstName || ''} ${pm.lastName || ''}`.trim() || pm.email || 'N/A';
        }
        return 'N/A';
    };

    // Get unit names
    const getUnitNames = () => {
        if (contractDetails?.unitDetails?.length > 0) {
            return contractDetails.unitDetails.map(unit => 
                `${unit.name} - ${unit.unitType} (${unit.division})`
            ).join(', ');
        }
        return 'No apartments assigned';
    };

    // Get client name
    const getClientName = () => {
        if (contractDetails?.customer) {
            return `${contractDetails.customer.firstName} ${contractDetails.customer.lastName}`;
        }
        return 'N/A';
    };

    // Get GL account name by ID
    const getGLAccountName = (accountId) => {
        if (!accountId) return 'N/A';
        
        const account = glAccounts.find(acc => acc._id === accountId);
        if (account) {
            return `${account.accountCode} - ${account.accountName} (${account.accountType})`;
        }
        
        // If account not found, show the ID as fallback
        return `Account ID: ${accountId}`;
    };

    // Check if we have lease data - Updated to use actual schema fields
    const hasLeaseData = contractDetails && contractDetails.startDate && contractDetails.endDate && contractDetails.paymentDueDate;

    // Dialog footer
    const dialogFooter = (
        <div className="d-flex justify-content-end">
            <Button 
                label="Close" 
                icon="pi pi-times" 
                onClick={onHide} 
                className="p-button-secondary"
            />
        </div>
    );

    if (!visible) return null;

    return (
        <Dialog
            visible={visible}
            onHide={onHide}
            header={`Contract Details - ${contractDetails?.contractName || 'Loading...'}`}
            footer={dialogFooter}
            className="contract-details-dialog"
            style={{ width: '90vw', maxWidth: '900px' }}
            modal
        >
            {loading ? (
                <div className="text-center py-5">
                    <i className="pi pi-spin pi-spinner text-primary" style={{ fontSize: '2rem' }}></i>
                    <p className="mt-2">Loading contract details...</p>
                </div>
            ) : contractDetails ? (
                <div className="contract-details-content" style={{ maxHeight: '75vh', overflowY: 'auto' }}>
                    
                    {/* Status Alert */}
                    {hasLeaseData && (
                        <div className="alert alert-success mb-4">
                            <i className="pi pi-check-circle mr-2"></i>
                            <strong>Lease Data Available:</strong> This contract is linked to an existing lease agreement.
                        </div>
                    )}
                    
                    {!hasLeaseData && (
                        <div className="alert alert-warning mb-4">
                            <i className="pi pi-exclamation-triangle mr-2"></i>
                            <strong>No Lease Data:</strong> This contract is not linked to a lease agreement.
                        </div>
                    )}

                    {/* Basic Information */}
                    <div className="card mb-4">
                        <div className="card-header">
                            <h6 className="mb-0">Basic Information</h6>
                        </div>
                        <div className="card-body">
                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <div className="field">
                                        <label className="font-semibold text-gray-700 text-sm">Contract Name</label>
                                        <div className="text-gray-600">{contractDetails.contractName || 'N/A'}</div>
                                    </div>
                                </div>
                                <div className="col-md-6 mb-3">
                                    <div className="field">
                                        <label className="font-semibold text-gray-700 text-sm">Property Manager</label>
                                        <div className="text-gray-600">{getPropertyManagerName()}</div>
                                    </div>
                                </div>
                                <div className="col-md-6 mb-3">
                                    <div className="field">
                                        <label className="font-semibold text-gray-700 text-sm">Status</label>
                                        <div><StatusBadge status={contractDetails.status} /></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* apartment & Client Information */}
                    <div className="card mb-4">
                        <div className="card-header">
                            <h6 className="mb-0">Apartment & Client Information</h6>
                        </div>
                        <div className="card-body">
                            <div className="row">
                                <div className="col-md-12 mb-3">
                                    <div className="field">
                                        <label className="font-semibold text-gray-700 text-sm">Apartments Managed</label>
                                        <div className="text-gray-600">{getUnitNames()}</div>
                                    </div>
                                </div>
                                <div className="col-md-6 mb-3">
                                    <div className="field">
                                        <label className="font-semibold text-gray-700 text-sm">Client</label>
                                        <div className="text-gray-600">{getClientName()}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Management Fee Configuration */}
                    <div className="card mb-4">
                        <div className="card-header">
                            <h6 className="mb-0">Management Fee Configuration</h6>
                        </div>
                        <div className="card-body">
                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <div className="field">
                                        <label className="font-semibold text-gray-700 text-sm">Management Fee Type</label>
                                        <div className="text-gray-600">
                                            {contractDetails.managementFee?.type === 'percentage' 
                                                ? 'Percentage of Rent' 
                                                : 'Fixed Amount'
                                            }
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-6 mb-3">
                                    <div className="field">
                                        <label className="font-semibold text-gray-700 text-sm">Management Fee Value</label>
                                        <div className="text-gray-600">
                                            {contractDetails.managementFee?.type === 'percentage' 
                                                ? `${contractDetails.managementFee?.value || 0}%` 
                                                : formatCurrency(contractDetails.managementFee?.value)
                                            }
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Contract Terms (Lease-Synced Fields) - Updated to match actual schema */}
                    <div className="card mb-4">
                        <div className="card-header">
                            <h6 className="mb-0">Contract Terms (Lease-Synced Fields)</h6>
                            {hasLeaseData ? (
                                <small className="text-success">
                                    <i className="pi pi-check mr-1"></i>
                                    These fields are populated from the linked lease agreement
                                </small>
                            ) : (
                                <small className="text-warning">
                                    <i className="pi pi-exclamation-triangle mr-1"></i>
                                    These fields will be available when a lease agreement is linked
                                </small>
                            )}
                        </div>
                        <div className="card-body">
                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <div className="field">
                                        <label className="font-semibold text-gray-700 text-sm">Start Date</label>
                                        <div className="text-gray-600">{formatDate(contractDetails.startDate)}</div>
                                    </div>
                                </div>
                                <div className="col-md-6 mb-3">
                                    <div className="field">
                                        <label className="font-semibold text-gray-700 text-sm">End Date</label>
                                        <div className="text-gray-600">{formatDate(contractDetails.endDate)}</div>
                                    </div>
                                </div>
                                <div className="col-md-6 mb-3">
                                    <div className="field">
                                        <label className="font-semibold text-gray-700 text-sm">Payment Due Date</label>
                                        <div className="text-gray-600">
                                            {contractDetails.paymentDueDate 
                                                ? `${contractDetails.paymentDueDate}${contractDetails.paymentDueDate === 1 ? 'st' : contractDetails.paymentDueDate === 2 ? 'nd' : contractDetails.paymentDueDate === 3 ? 'rd' : 'th'} of month`
                                                : 'N/A'
                                            }
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-6 mb-3">
                                    <div className="field">
                                        <label className="font-semibold text-gray-700 text-sm">Payment Frequency</label>
                                        <div className="text-gray-600">{contractDetails.frequency || 'N/A'}</div>
                                    </div>
                                </div>
                                <div className="col-md-6 mb-3">
                                    <div className="field">
                                        <label className="font-semibold text-gray-700 text-sm">Next Invoice Date</label>
                                        <div className="text-gray-600">{formatDate(contractDetails.nextInvoiceDate)}</div>
                                    </div>
                                </div>
                                <div className="col-md-6 mb-3">
                                    <div className="field">
                                        <label className="font-semibold text-gray-700 text-sm">Balance Brought Forward</label>
                                        <div className="text-gray-600">{formatCurrency(contractDetails.balanceBroughtForward)}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* GL Account Configuration */}
                    {contractDetails.glAccounts && (
                        <div className="card mb-4">
                            <div className="card-header">
                                <h6 className="mb-0">GL Account Configuration</h6>
                            </div>
                            <div className="card-body">
                                <div className="row">
                                    <div className="col-md-6">
                                        <h6 className="text-muted mb-3">Invoice GL Entries</h6>
                                        <div className="mb-3">
                                            <div className="field">
                                                <label className="font-semibold text-gray-700 text-sm">Invoice Debit Account</label>
                                                <div className="text-gray-600">{getGLAccountName(contractDetails.glAccounts.invoice?.debit)}</div>
                                            </div>
                                        </div>
                                        <div className="mb-3">
                                            <div className="field">
                                                <label className="font-semibold text-gray-700 text-sm">Invoice Credit Account</label>
                                                <div className="text-gray-600">{getGLAccountName(contractDetails.glAccounts.invoice?.credit)}</div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <h6 className="text-muted mb-3">Payment GL Entries</h6>
                                        <div className="mb-3">
                                            <div className="field">
                                                <label className="font-semibold text-gray-700 text-sm">Payment Debit Account</label>
                                                <div className="text-gray-600">{getGLAccountName(contractDetails.glAccounts.payment?.debit)}</div>
                                            </div>
                                        </div>
                                        <div className="mb-3">
                                            <div className="field">
                                                <label className="font-semibold text-gray-700 text-sm">Payment Credit Account</label>
                                                <div className="text-gray-600">{getGLAccountName(contractDetails.glAccounts.payment?.credit)}</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {!contractDetails.glAccounts && (
                        <div className="card mb-4">
                            <div className="card-header">
                                <h6 className="mb-0">GL Account Configuration</h6>
                            </div>
                            <div className="card-body">
                                <div className="alert alert-info">
                                    <i className="pi pi-info-circle mr-2"></i>
                                    No GL accounts configured for this contract.
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Termination Details (if terminated) */}
                    {contractDetails.status === 'Terminated' && (
                        <div className="card mb-4">
                            <div className="card-header">
                                <h6 className="mb-0">Termination Details</h6>
                            </div>
                            <div className="card-body">
                                <div className="row">
                                    <div className="col-md-6 mb-3">
                                        <div className="field">
                                            <label className="font-semibold text-gray-700 text-sm">Termination Date</label>
                                            <div className="text-gray-600">{formatDate(contractDetails.terminationDate)}</div>
                                        </div>
                                    </div>
                                    <div className="col-md-12 mb-3">
                                        <div className="field">
                                            <label className="font-semibold text-gray-700 text-sm">Termination Reason</label>
                                            <div className="text-gray-600">{contractDetails.terminationReason || 'N/A'}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Record Information */}
                    <div className="card mb-4">
                        <div className="card-header">
                            <h6 className="mb-0">Record Information</h6>
                        </div>
                        <div className="card-body">
                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <div className="field">
                                        <label className="font-semibold text-gray-700 text-sm">Created Date</label>
                                        <div className="text-gray-600">{formatDate(contractDetails.createdAt)}</div>
                                    </div>
                                </div>
                                <div className="col-md-6 mb-3">
                                    <div className="field">
                                        <label className="font-semibold text-gray-700 text-sm">Last Updated</label>
                                        <div className="text-gray-600">{formatDate(contractDetails.updatedAt)}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="text-center py-5">
                    <i className="pi pi-exclamation-triangle text-warning" style={{ fontSize: '2rem' }}></i>
                    <p className="mt-2">No contract details available</p>
                </div>
            )}
        </Dialog>
    );
};

export default PropertyManagerContractDetailsDialog;